import { useAtomValue } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { NoImage } from 'macaron-frontend-ui'
import type { FC } from 'react'
import Image from '~/components/image/Image'
import { restaurantId$ } from '~/features/guide/KiwaminoArticles'
import { money } from '~/lib/money/money'
import { css, sva } from '~/styled-system/css'
import { GuideSection } from '../GuideSection'
import type { OtoriyoseProduct } from './types'

const otoriyoseProducts$ = atomWithQuery<OtoriyoseProduct[]>((get) => {
  return {
    queryKey: ['otoriyose-products', get(restaurantId$)],
    // loader から hydration で得るので空でよい
    queryFn: async () => [],
    gcTime: 1000 * 60 * 60 * 24,
    staleTime: 1000 * 60 * 60 * 24,
  }
})

export const IkyuOnlyOtoriyoseProducts: FC =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyOtoriyoseProducts : OtoriyoseProducts

function EmptyOtoriyoseProducts() {
  return null
}

function OtoriyoseProducts() {
  const restaurantId = useAtomValue(restaurantId$)
  const { data: products } = useAtomValue(otoriyoseProducts$)
  if (products === undefined || products.length === 0) {
    return null
  }

  return (
    // FIXME: 上位のコンポーネントでpaddingを設定するべき
    //        <https://github.com/ikyucom/macaron-frontend/issues/4517>
    <div className={css({ py: '3', _pc: { py: '0' } })}>
      <GuideSection heading="この店舗のお取り寄せ" size="large">
        {/* このコンポーネントは横スクロールのあるコンポーネントであり、keyを設定しないとガイド間遷移でスクロール位置が維持されてしまう
        これを回避するため、RecommendRestaurants.tsxと同様に key を設定する */}
        <ul
          className={css({
            position: 'relative',
            display: 'flex',
            columnGap: '3',
            w: 'full',
            _sd: { overflowX: 'auto', pb: '2', px: '3' },
          })}
          key={restaurantId}
        >
          {products.map((product) => {
            return (
              <li key={product.url}>
                <OtoriyoseProductCard product={product} />
              </li>
            )
          })}
        </ul>
      </GuideSection>
    </div>
  )
}

const styles = sva({
  slots: ['container', 'image', 'content', 'name', 'price'],
  base: {
    container: {
      display: 'block',
      width: '[160px]',
      overflow: 'hidden',
      borderRadius: 'md',
      shadow: 'outline.sm',
      _pc: {
        width: '[156px]',
      },
    },
    image: {
      height: '[96px]',
      width: 'full',
      objectFit: 'cover',
    },
    content: {
      display: 'flex',
      width: 'full',
      padding: '2',
      flexDirection: 'column',
      color: 'gray.700',
      fontSize: 'sm',
    },
    name: {
      lineClamp: '2',
    },
    price: {
      marginTop: 'auto',
      paddingTop: '2',
      textAlign: 'right',
    },
  },
})

function OtoriyoseProductCard({ product }: { product: OtoriyoseProduct }) {
  const classes = styles()
  return (
    <a href={product.url} target="_blank" className={classes.container} rel="noopener noreferrer">
      {product.image ? (
        <Image
          src={product.image.url}
          alt={product.image.name}
          width={160}
          height={100}
          className={classes.image}
        />
      ) : (
        <NoImage width={160} height={100} />
      )}
      <div className={classes.content}>
        <p className={classes.name}>{product.name}</p>
        <p className={classes.price}>{money(product.price)}円</p>
      </div>
    </a>
  )
}
